import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Tag } from "@/components/ui/tag"
import { BetterDocRecommendationDialog } from "@/modules/admin/better-doc/components/better-doc-recommendation-dialog"
import { BetterDocSessionLink } from "@/modules/admin/better-doc/components/better-doc-session-link"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import {
  BetterDocOpenListKind,
  EMPTY_VALUE_PLACEHOLDER,
} from "@/modules/admin/better-doc/constants/better-doc-constants"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { formatBetterDocValue, isBetterDocTextPresent } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocOpenRecommendation } from "@/modules/admin/better-doc/services/betterDocRecommendedRecommendationsQuery"

type BetterDocOpenRecommendationTableProps = {
  recommendations: ReadonlyArray<BetterDocOpenRecommendation>
  listKind: BetterDocOpenListKind
}

const formatSelectedTarget = (recommendation: BetterDocOpenRecommendation) => {
  const selectedName = recommendation.selectedMedic?.name ?? null
  const selectionReference = [recommendation.selectionType, recommendation.selectionId]
    .filter((selectionPart) => selectionPart !== null && selectionPart !== "")
    .join(" ")

  return (
    <div className="flex flex-col">
      <span>{isBetterDocTextPresent(selectedName) ? selectedName : EMPTY_VALUE_PLACEHOLDER}</span>
      {selectionReference === "" ? null : <span className="text-on-surface-subdued text-xs">{selectionReference}</span>}
    </div>
  )
}

export const BetterDocOpenRecommendationTable = ({
  recommendations,
  listKind,
}: BetterDocOpenRecommendationTableProps) => {
  const { t } = useTranslation()
  const [detailsRecommendationId, setDetailsRecommendationId] = useState<string | null>(null)

  if (recommendations.length === 0) {
    return (
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-open-recommendation-table-empty">
        {t("admin.betterDoc.openRecommendations.empty")}
      </p>
    )
  }

  return (
    <>
      <BetterDocTable data-testid="better-doc-admin-open-recommendation-table">
        <thead>
          <tr>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.openRecommendations.recommendationId")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.openRecommendations.state")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.openRecommendations.version")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.openRecommendations.partnerPatientId")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.openRecommendations.partnerSessionId")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.recommendationDetails.sofiaSession")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {listKind === "recommended"
                ? t("admin.betterDoc.openRecommendations.medics")
                : t("admin.betterDoc.openRecommendations.selection")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              <span className="sr-only">{t("admin.betterDoc.openRecommendations.details")}</span>
            </BetterDocTableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {recommendations.map((recommendation) => (
            <tr
              key={recommendation.recommendationId}
              className="hover:bg-surface-hovered"
              data-testid="better-doc-admin-open-recommendation-row">
              <BetterDocTableCell className="break-all">
                {formatBetterDocValue(recommendation.recommendationId)}
              </BetterDocTableCell>
              <BetterDocTableCell>
                {recommendation.state === null ? (
                  EMPTY_VALUE_PLACEHOLDER
                ) : (
                  <Tag hasIcon={false} label={getBetterDocEnumLabel(t, "recommendationState", recommendation.state)} />
                )}
              </BetterDocTableCell>
              <BetterDocTableCell>{formatBetterDocValue(recommendation.version)}</BetterDocTableCell>
              <BetterDocTableCell className="break-all">
                {formatBetterDocValue(recommendation.partnerPatientId)}
              </BetterDocTableCell>
              <BetterDocTableCell className="break-all">
                {formatBetterDocValue(recommendation.partnerSessionId)}
              </BetterDocTableCell>
              <BetterDocTableCell>
                {recommendation.localSessionId === null ? (
                  t("admin.betterDoc.recommendationDetails.notInThisEnvironment")
                ) : (
                  <BetterDocSessionLink sessionId={recommendation.localSessionId} />
                )}
              </BetterDocTableCell>
              <BetterDocTableCell>
                {listKind === "recommended" ? recommendation.medics.length : formatSelectedTarget(recommendation)}
              </BetterDocTableCell>
              <BetterDocTableCell>
                {recommendation.recommendationId === null ? null : (
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={() => setDetailsRecommendationId(recommendation.recommendationId)}
                    data-testid="better-doc-admin-open-recommendation-details-button">
                    {t("admin.betterDoc.openRecommendations.details")}
                  </Button>
                )}
              </BetterDocTableCell>
            </tr>
          ))}
        </tbody>
      </BetterDocTable>
      {detailsRecommendationId === null ? null : (
        <BetterDocRecommendationDialog
          recommendationId={detailsRecommendationId}
          onClose={() => setDetailsRecommendationId(null)}
        />
      )}
    </>
  )
}
